class TagBasedProductFilter {
  constructor() {
    this.shopDomain = window.SHOP_DOMAIN;
    this.storefrontToken = window.STOREFRONT_TOKEN;
    this.endpoint = `https://${this.shopDomain}/api/2025-10/graphql.json`;
    this.filters = {
      productTypes: [],
      vendors: [],
      colors: []
    };
    this.products = [];
    this.productsPerPage = 70;
    this.hasNextPage = false;
    this.endCursor = null;
    this.isLoading = false;
    
    this.init();
  }

  init() {
    if (!document.getElementById('filtered-products')) {
      console.error('filtered-products container not found!');
      return;
    }
    
    console.log('Tag-based filter initialized');
    
    this.setupFilterToggle();
    this.setupFilterAccordions();
    this.loadAvailableFilters();
    this.loadProducts();
    this.attachEventListeners();
  }

  setupFilterAccordions() {
    const filterToggles = document.querySelectorAll('[data-filter-toggle]');
    
    filterToggles.forEach(toggle => {
      if (toggle.dataset.accordionSetup) return;
      toggle.dataset.accordionSetup = 'true';
      
      toggle.addEventListener('click', (e) => {
        const filterGroup = toggle.closest('.filter-group');
        filterGroup.classList.toggle('expanded');
      });
    });
  }

  setupFilterToggle() {
    const toggleBtn = document.getElementById('filter-toggle');
    const container = document.getElementById('collection-container');
    
    if (!toggleBtn || !container) return;
    
    toggleBtn.addEventListener('click', () => {
      container.classList.toggle('filters-open');
      const isOpen = container.classList.contains('filters-open');
      toggleBtn.querySelector('.filter-icon').textContent = isOpen ? '⊕' : '⊖';
    });
  }

  async loadAvailableFilters() {
    // Load all unique brands and colors for filter options
    try {
      const query = `{
        products(first: 250, query: "status:active") {
          edges {
            node {
              vendor
              tags
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }`;
      
      let allVendors = new Set();
      let allColors = new Set();
      let hasNextPage = true;
      let cursor = null;
      
      while (hasNextPage) {
        const afterParam = cursor ? `, after: "${cursor}"` : '';
        const paginatedQuery = query.replace('first: 250', `first: 250${afterParam}`);
        
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken
          },
          body: JSON.stringify({ query: paginatedQuery })
        });

        const data = await response.json();
        
        if (data.errors) {
          console.error('Error loading filters:', data.errors);
          break;
        }

        const products = data.data.products;
        
        products.edges.forEach(edge => {
          if (edge.node.vendor) {
            allVendors.add(edge.node.vendor);
          }
          
          // Extract Color tags
          edge.node.tags.forEach(tag => {
            if (tag.startsWith('Color:')) {
              const color = tag.replace('Color:', '').trim();
              allColors.add(color);
            }
          });
        });
        
        hasNextPage = products.pageInfo.hasNextPage;
        cursor = products.pageInfo.endCursor;
      }
      
      this.populateBrandFilters([...allVendors].sort());
      this.populateColorFilters([...allColors].sort());
      
      console.log(`Loaded ${allVendors.size} brands and ${allColors.size} colors`);
      
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  }

  populateBrandFilters(vendors) {
    const brandContainer = document.getElementById('brand-filters');
    if (!brandContainer) return;
    
    if (vendors.length === 0) {
      brandContainer.innerHTML = '<div class="loading-filters">No brands found</div>';
      return;
    }
    
    brandContainer.innerHTML = vendors.map(vendor => `
      <label class="filter-option">
        <input type="checkbox" value="${vendor}" data-filter="vendor">
        <span>${vendor}</span>
      </label>
    `).join('');
    
    brandContainer.querySelectorAll('[data-filter="vendor"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.filters.vendors.push(e.target.value);
        } else {
          this.filters.vendors = this.filters.vendors.filter(v => v !== e.target.value);
        }
        this.loadProducts();
      });
    });
  }

  populateColorFilters(colors) {
    const colorContainer = document.getElementById('color-filters');
    if (!colorContainer) return;
    
    if (colors.length === 0) {
      colorContainer.innerHTML = '<div class="loading-filters">No colors found</div>';
      return;
    }
    
    colorContainer.innerHTML = colors.map(color => `
      <label class="filter-option">
        <input type="checkbox" value="${color}" data-filter="color">
        <span>${color}</span>
      </label>
    `).join('');
    
    colorContainer.querySelectorAll('[data-filter="color"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.filters.colors.push(e.target.value);
        } else {
          this.filters.colors = this.filters.colors.filter(c => c !== e.target.value);
        }
        this.loadProducts();
      });
    });
  }

  attachEventListeners() {
    document.querySelectorAll('[data-filter="productType"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.filters.productTypes.push(e.target.value);
        } else {
          this.filters.productTypes = this.filters.productTypes.filter(t => t !== e.target.value);
        }
        this.loadProducts();
      });
    });
  }

  buildQuery(loadMore = false) {
    const queryParts = [];
    
    // Helper function to format values with spaces
    const formatValue = (value) => {
      return value.includes(' ') || value.includes('-') ? `"${value}"` : value;
    };
    
    // Add status filter
    queryParts.push('status:active');
    
    // Product types
    if (this.filters.productTypes.length > 0) {
      const types = this.filters.productTypes
        .map(t => `product_type:${formatValue(t)}`)
        .join(' OR ');
      queryParts.push(`(${types})`);
    }
    
    // Vendors
    if (this.filters.vendors.length > 0) {
      const vendors = this.filters.vendors
        .map(v => `vendor:${formatValue(v)}`)
        .join(' OR ');
      queryParts.push(`(${vendors})`);
    }
    
    // Colors (using tags)
    if (this.filters.colors.length > 0) {
      const colorTags = this.filters.colors
        .map(c => `tag:Color\\:${c}`)  // Escape the colon
        .join(' OR ');
      queryParts.push(`(${colorTags})`);
    }
    
    const searchQuery = queryParts.join(' AND ');
    const afterCursor = loadMore && this.endCursor ? `, after: "${this.endCursor}"` : '';
    
    return `{
      products(first: ${this.productsPerPage}, query: "${searchQuery}"${afterCursor}) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title
            handle
            productType
            vendor
            tags
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  availableForSale
                }
              }
            }
          }
        }
      }
    }`;
  }

  async loadProducts(loadMore = false) {
    if (this.isLoading) return;
    this.isLoading = true;
    
    const container = document.getElementById('filtered-products');
    
    if (!loadMore) {
      container.innerHTML = '<div class="loading">Loading products...</div>';
      this.products = [];
      this.endCursor = null;
    } else {
      container.classList.add('filtering');
    }
    
    try {
      const query = this.buildQuery(loadMore);
      
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({ query })
      });

      const data = await response.json();
      
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        container.innerHTML = '<div class="error">Error loading products. Please try again.</div>';
        this.isLoading = false;
        return;
      }

      const productsData = data.data.products;
      
      this.products = loadMore 
        ? [...this.products, ...productsData.edges]
        : productsData.edges;
      
      this.hasNextPage = productsData.pageInfo.hasNextPage;
      this.endCursor = productsData.pageInfo.endCursor;
      
      console.log(`Loaded ${this.products.length} products, hasNextPage: ${this.hasNextPage}`);
      
      container.classList.remove('filtering');
      this.renderProducts();
      
    } catch (error) {
      console.error('Error loading products:', error);
      container.innerHTML = '<div class="error">Error loading products. Please try again.</div>';
    }
    
    this.isLoading = false;
  }

  renderProducts() {
    const container = document.getElementById('filtered-products');
    
    if (this.products.length === 0) {
      container.innerHTML = '<p class="no-results">No products found matching your filters.</p>';
      return;
    }

    const productsHTML = this.products.map(({ node: product }) => {
      const image = product.images.edges[0]?.node.url || '';
      const price = parseFloat(product.priceRange.minVariantPrice.amount);
      const priceFormatted = price.toFixed(2);
      const available = product.variants.edges[0]?.node.availableForSale;
      
      const compareAtPrice = product.compareAtPriceRange?.minVariantPrice?.amount 
        ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount) 
        : null;
      const isOnSale = compareAtPrice && compareAtPrice > price;
      const discountPercent = isOnSale 
        ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
        : 0;
      
      const priceParts = priceFormatted.split('.');
      const dollars = priceParts[0];
      const cents = priceParts[1];
      
      let compareDollars = '';
      let compareCents = '';
      if (isOnSale) {
        const compareParts = compareAtPrice.toFixed(2).split('.');
        compareDollars = compareParts[0];
        compareCents = compareParts[1];
      }
      
      const productId = product.id.split('/').pop();
      
      return `
<li class="grid__item">
  <link href="//modecraze.com/cdn/shop/t/20/assets/component-rating.css?v=173409050425969898561729210444" rel="stylesheet" type="text/css" media="all">

  <div class="card-wrapper">
    <use-animate data-animate="zoom-fade-small" class="card card--product" tabindex="-1" animate="">
      <a href="/products/${product.handle}" class="card__media media-wrapper" tabindex="-1">
        <div class="card--image-animate image-animate media media--portrait media--hover-effect media--image-contain">
          <picture is="prog-picture" class="motion-reduce">
            <img src="${image}" 
                 alt="${product.title}" 
                 srcset="${image}&width=165 165w, ${image}&width=360 360w, ${image}&width=535 535w, ${image}&width=750 750w, ${image}&width=940 940w, ${image}&width=1100 1100w"
                 width="1100" 
                 height="1467" 
                 loading="lazy"
                 class="motion-reduce" 
                 sizes="(min-width: 1600px) 367px, (min-width: 990px) calc((100vw - 10rem) / 4), (min-width: 750px) calc((100vw - 10rem) / 3), calc(62vw - 3rem)"
                 is="lazy-image">
          </picture>
        </div>
      </a>
    </use-animate>

    <div class="card-information">
      <a href="/products/${product.handle}" class="full-unstyled-link">
        <span class="visually-hidden">${product.title}</span>
      </a>
      <div class="card-information__wrapper">
        <div class="card-information__top">
          <span class="visually-hidden">Vendor:</span>
          <div class="card-article-info caption-with-letter-spacing">${product.vendor}</div>
          <div class="card__colors card__colors--small card__colors--${productId}"></div>
          <style>
            .card__colors--${productId} {
              display: none !important;
            }
          </style>
        </div>
        
        <a href="/products/${product.handle}" class="card-information__text h4" tabindex="-1">
          ${product.title}
        </a>

        <span class="caption-large light"></span>
        
        <div class="sizes_holder">
          <div class="show_sizes" style="display: none;">
            <span class="visible_sizes">
              <meta name="format-detection" content="telephone=no">
            </span>
          </div>
          ${!available || isOnSale ? `
          <div class="card__badge" style="display: block;">
            <span class="badge ${!available ? 'badge--sold-out' : 'badge--onsale'}" aria-hidden="true">
              ${!available ? 'Sold Out' : `–${discountPercent}%`}
            </span>
          </div>
          ` : '<div class="card__badge" style="display: none;"></div>'}
        </div>

        <div class="price${isOnSale ? ' price--on-sale' : ''}">
          <dl>
            ${isOnSale ? `
            <div class="price__regular">
              <dt>
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
              </dt>
              <dd>
                <span class="price-item price-item--regular">
                  <price-money>
                    <bdi>
                      <span class="price__prefix">$</span>${dollars}<sup class="price__suffix">.${cents}</sup>
                    </bdi>
                  </price-money>
                </span>
              </dd>
            </div>
            <div class="price__sale">
              <dt class="price__compare">
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
              </dt>
              <dd class="price__compare">
                <s class="price-item price-item--regular">
                  <price-money>
                    <bdi>
                      <span class="price__prefix">$</span>${compareDollars}<sup class="price__suffix">.${compareCents}</sup>
                    </bdi>
                  </price-money>
                </s>
              </dd>
              <dt>
                <span class="visually-hidden visually-hidden--inline">Sale price</span>
              </dt>
              <dd>
                <span class="price-item price-item--sale">
                  <price-money>
                    <bdi>
                      <span class="price__prefix">$</span>${dollars}<sup class="price__suffix">.${cents}</sup>
                    </bdi>
                  </price-money>
                </span>
              </dd>
            </div>
            ` : `
            <div class="price__regular">
              <dt>
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
              </dt>
              <dd>
                <span class="price-item price-item--regular">
                  <price-money>
                    <bdi>
                      <span class="price__prefix">$</span>${dollars}<sup class="price__suffix">.${cents}</sup>
                    </bdi>
                  </price-money>
                </span>
              </dd>
            </div>
            `}
          </dl>
        </div>
      </div>

      <div class="card-information__button"></div>
    </div>
  </div>
</li>
      `;
    }).join('');

    const loadMoreButton = this.hasNextPage 
      ? `<div class="load-more-wrapper" style="text-align: center; padding: 3rem 0;">
           <button id="load-more-btn" class="button" style="padding: 1rem 2rem; font-size: 1rem; background: #000; color: #fff; border: none; cursor: pointer; min-width: 200px;">
             Load More Products
           </button>
         </div>` 
      : '';

    container.innerHTML = `
      <div id="ProductGridContainer">
        <div class="collection">
          <div class="loading-overlay"></div>
          <ul id="product-grid" class="product-grid grid grid--2-col grid--3-col-tablet grid--4-col-desktop" role="list">
            ${productsHTML}
          </ul>
        </div>
      </div>
      ${loadMoreButton}
    `;

    if (this.hasNextPage) {
      const loadMoreBtn = document.getElementById('load-more-btn');
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', () => {
          this.loadProducts(true);
        });
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TagBasedProductFilter();
  });
} else {
  new TagBasedProductFilter();
}